import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Checkbox, Chip, Container, FormControlLabel, Grid,
  Paper, Skeleton, Stack, Typography,
} from "@mui/material";
import {
  AddShoppingCart, ArrowBack, ArrowForward, CheckCircle, LockOpen, LockOutlined, MenuBook, OndemandVideo,
} from "@mui/icons-material";
import { DP } from "../components/home/HomeStyles";
import coursesService, { type LeafletCatalogItem } from "../services/coursesService";
import { apiClient } from "../services/authService";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { localizedDescription, localizedTitle } from "../utils/localizedContent";
import {
  PLAN_TERMS, VIDEO_ADDON_PER_COURSE, basePriceOf, computePlanPrice, maxCoursesOf,
} from "../utils/planPricing";
import { loadLeafletCart, saveLeafletCart } from "../utils/leafletCart";

/** Plans that let the buyer pick their leaflets; the rest skip this page. */
const PICKABLE_PLANS = ["starter", "pro"] as const;
type PickablePlan = (typeof PICKABLE_PLANS)[number];

const isPickablePlan = (planId?: string): planId is PickablePlan =>
  !!planId && (PICKABLE_PLANS as readonly string[]).includes(planId) && !!PLAN_TERMS[planId];

/**
 * Storefront step between a pricing card and checkout: every leaflet is shown
 * locked with its full summary, so the buyer knows what each one contains
 * before choosing which to unlock.
 */
const ChooseLeaflets: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, isRTL, language } = useLanguage();
  const tc = t.courseSelection;
  const BackIcon    = isRTL ? ArrowForward : ArrowBack;
  const ForwardIcon = isRTL ? ArrowBack : ArrowForward;

  const [leaflets,  setLeaflets]  = useState<LeafletCatalogItem[]>([]);
  const [ownedIds,  setOwnedIds]  = useState<string[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [failed,    setFailed]    = useState(false);
  const maxCourses = isPickablePlan(planId) ? maxCoursesOf(planId) : 1;

  // Start from the saved cart. Coming from another plan keeps as many picks
  // as this one allows, so switching plans doesn't throw the choice away.
  const [selected, setSelected] = useState<string[]>(
    () => (loadLeafletCart()?.courseIds ?? []).slice(0, maxCourses)
  );
  const [withVideo, setWithVideo] = useState<string[]>(() => {
    const cart = loadLeafletCart();
    const kept = (cart?.courseIds ?? []).slice(0, maxCourses);
    return (cart?.videoCourseIds ?? []).filter(id => kept.includes(id));
  });

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [catalog, sub] = await Promise.all([
        coursesService.getCatalog(),
        // Visitors have no subscription to look up — asking anyway would trip
        // the session-expired handling for a session that never existed.
        user ? apiClient.get("/subscriptions/me").catch(() => null) : Promise.resolve(null),
      ]);
      const available = catalog.courses || [];
      const owned: string[] = sub?.data?.data?.selectedCourseIds?.map((id: any) => String(id)) ?? [];
      setLeaflets(available);
      setOwnedIds(owned);
      // Drop saved picks that were unpublished since, or that the buyer now owns.
      const buyable = (id: string) => !owned.includes(id) && available.some(l => l._id === id);
      setSelected(prev => prev.filter(buyable));
      setWithVideo(prev => prev.filter(buyable));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { load(); }, [load]);

  // Keep the cart in step with every change, once the catalog has been checked.
  useEffect(() => {
    if (loading || !isPickablePlan(planId)) return;
    saveLeafletCart({ planId, courseIds: selected, videoCourseIds: withVideo });
  }, [loading, planId, selected, withVideo]);

  // With a single slot, picking another leaflet replaces the current one, so
  // no card is ever out of reach.
  const singleSelect = maxCourses === 1;
  const atLimit = !singleSelect && selected.length >= maxCourses;

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = prev.includes(id)
        ? prev.filter(x => x !== id)
        : singleSelect
        ? [id]
        : prev.length >= maxCourses ? prev : [...prev, id];
      // A video add-on can't outlive the leaflet it belongs to — the server
      // rejects an order whose video ids aren't all in the selection.
      setWithVideo(v => v.filter(x => next.includes(x)));
      return next;
    });
  };

  const toggleVideo = (id: string) =>
    setWithVideo(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  // Selection order, so the summary and checkout list read the way they were picked.
  const selectedLeaflets = useMemo(
    () => selected.map(id => leaflets.find(l => l._id === id)).filter(Boolean) as LeafletCatalogItem[],
    [selected, leaflets]
  );

  if (!isPickablePlan(planId)) return <Navigate to="/" replace />;

  const planName = t.home.pricing.plans[planId].name;
  const price = computePlanPrice({ planId, videoCourseIds: withVideo });

  const backToPlans = () => {
    navigate("/");
    setTimeout(() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }), 200);
  };

  const handleContinue = () => {
    const titles = selectedLeaflets.map(l => localizedTitle(l, language));
    const paymentState = {
      planId,
      planName,
      // The running total, video add-ons included — checkout sends this and
      // the server rejects it if it doesn't match the catalog.
      price,
      billing: "monthly",
      selectedCourseIds: selectedLeaflets.map(l => l._id),
      selectedCourseTitles: titles,
      videoCourseIds: withVideo,
    };
    // Checkout creates the account itself, so visitors go straight there.
    // The session copy lets the order survive a refresh on the checkout page.
    sessionStorage.setItem("pending_payment_state", JSON.stringify(paymentState));
    navigate("/payment", { state: paymentState });
  };

  const renderCard = (leaflet: LeafletCatalogItem) => {
    const isOwned    = ownedIds.includes(leaflet._id);
    const isSelected = selected.includes(leaflet._id);
    const isBlocked  = !isOwned && !isSelected && atLimit;
    const hasVideo   = withVideo.includes(leaflet._id);
    const offersVideo = leaflet.companionVideoCount > 0;

    return (
      <Grid key={leaflet._id} size={{ xs: 12, md: 6 }}>
        <Paper elevation={0} sx={{
          height: "100%", display: "flex", flexDirection: "column",
          borderRadius: "22px", overflow: "hidden", bgcolor: DP.white,
          border: `2px solid ${isSelected ? DP.blue : DP.border}`,
          boxShadow: isSelected ? `0 18px 40px ${DP.shadow}` : "0 4px 20px rgba(0,0,0,0.04)",
          transition: "border-color .25s ease, box-shadow .25s ease",
        }}>
          {/* Cover — locked until bought; opens up once selected */}
          <Box sx={{ position: "relative", height: { xs: 170, sm: 200 } }}>
            <Box sx={{
              position: "absolute", inset: 0,
              background: leaflet.thumbnail
                ? `url(${leaflet.thumbnail}) center/cover no-repeat`
                : `linear-gradient(135deg, ${DP.blue}, ${DP.blueLight})`,
              filter: isSelected || isOwned ? "none" : "grayscale(55%) brightness(0.75)",
              transition: "filter .3s ease",
            }} />
            <Box sx={{
              position: "absolute", inset: 0,
              bgcolor: isSelected || isOwned ? "rgba(10,22,40,0.15)" : "rgba(10,22,40,0.45)",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background-color .3s ease",
            }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: "50%",
                bgcolor: isSelected ? DP.blue : "rgba(255,255,255,0.18)",
                backdropFilter: "blur(4px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background-color .3s ease",
              }}>
                {isSelected || isOwned
                  ? <LockOpen sx={{ color: DP.white, fontSize: 26 }} />
                  : <LockOutlined sx={{ color: DP.white, fontSize: 26 }} />}
              </Box>
            </Box>
            {isOwned && (
              <Chip label={tc.owned} size="small" sx={{
                position: "absolute", top: 12, left: 12,
                bgcolor: "#10B981", color: DP.white, fontWeight: 700,
              }} />
            )}
          </Box>

          <Box sx={{ p: { xs: 2.25, sm: 2.75 }, display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <Typography component="h2" sx={{
              fontWeight: 800, color: DP.textDark, fontSize: { xs: "1.15rem", sm: "1.25rem" },
              lineHeight: 1.3, letterSpacing: "-.01em",
            }}>
              {localizedTitle(leaflet, language)}
            </Typography>

            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
              {leaflet.pageCount > 0 && (
                <Chip size="small" icon={<MenuBook sx={{ fontSize: "14px !important" }} />}
                  label={t.courseDetail.pagesMeta(leaflet.pageCount)}
                  sx={{ bgcolor: DP.blueBg, color: DP.blue, fontWeight: 700, "& .MuiChip-icon": { color: DP.blue } }} />
              )}
              {offersVideo && (
                <Chip size="small" icon={<OndemandVideo sx={{ fontSize: "14px !important" }} />}
                  label={tc.videoAvailable}
                  sx={{ bgcolor: DP.blueBg, color: DP.blue, fontWeight: 700, "& .MuiChip-icon": { color: DP.blue } }} />
              )}
            </Stack>

            {/* The full summary — never clamped, it is what the buyer decides on */}
            <Typography sx={{
              mt: 1.6, mb: 2.5, color: DP.textMid, lineHeight: 1.75, fontSize: ".93rem",
              whiteSpace: "pre-line",
            }}>
              {localizedDescription(leaflet, language) || tc.fallbackDescription}
            </Typography>

            <Box sx={{ mt: "auto" }}>
              {isOwned ? (
                <Button fullWidth variant="outlined"
                  onClick={() => navigate(`/membership/classes/${leaflet._id}`)}
                  sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, py: 1.1,
                    borderColor: "rgba(16,185,129,.5)", color: "#059669",
                    "&:hover": { borderColor: "#059669", bgcolor: "rgba(16,185,129,.06)" } }}>
                  {tc.openOwned}
                </Button>
              ) : (
                <>
                  <Button fullWidth
                    variant={isSelected ? "contained" : "outlined"}
                    disabled={isBlocked}
                    aria-pressed={isSelected}
                    onClick={() => toggle(leaflet._id)}
                    startIcon={isSelected ? <CheckCircle /> : <AddShoppingCart />}
                    sx={{
                      borderRadius: "12px", textTransform: "none", fontWeight: 700, py: 1.1,
                      ...(isSelected
                        ? { bgcolor: DP.blue, "&:hover": { bgcolor: DP.navy } }
                        : { borderColor: DP.bluePale, color: DP.blue,
                            "&:hover": { borderColor: DP.blue, bgcolor: DP.blueBg } }),
                    }}
                  >
                    {isSelected ? tc.selected : tc.select}
                  </Button>

                  {isSelected && offersVideo && (
                    <Box sx={{
                      mt: 1.2, px: 1.5, py: .5, borderRadius: "12px",
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
                      border: `1px solid ${hasVideo ? DP.blue : DP.border}`,
                      bgcolor: hasVideo ? DP.blueBg : "transparent",
                    }}>
                      <FormControlLabel
                        sx={{ m: 0, minWidth: 0 }}
                        control={
                          <Checkbox size="small" checked={hasVideo}
                            onChange={() => toggleVideo(leaflet._id)}
                            sx={{ color: "#CBD5E1", "&.Mui-checked": { color: DP.blue } }} />
                        }
                        label={
                          <Typography sx={{ fontSize: ".85rem", fontWeight: 600, color: DP.textDark }}>
                            {tc.withVideo}
                          </Typography>
                        }
                      />
                      <Typography sx={{ flexShrink: 0, fontSize: ".85rem", fontWeight: 800, color: hasVideo ? DP.blue : DP.textMuted }}>
                        {tc.withVideoHint(VIDEO_ADDON_PER_COURSE)}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  return (
    <Box sx={{ bgcolor: DP.offWhite, minHeight: "100vh", pt: "80px", pb: { xs: 18, sm: 14 } }}>
      {/* ── Header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${DP.navy} 0%, ${DP.navyMid} 100%)`,
        pt: { xs: 3, md: 5 }, pb: { xs: 4, md: 5 },
      }}>
        <Container maxWidth="lg">
          <Button startIcon={<BackIcon />} onClick={backToPlans}
            sx={{ color: "rgba(255,255,255,.7)", textTransform: "none", mb: 2, "&:hover": { color: DP.white } }}>
            {tc.back}
          </Button>
          <Chip
            label={`${planName} · $${basePriceOf(planId)} · ${tc.planLeaflets(maxCourses)}`}
            size="small"
            sx={{ bgcolor: "rgba(66,165,245,.2)", color: "#90CAF9", fontWeight: 700, mb: 1.5 }}
          />
          <Typography component="h1" sx={{
            color: DP.white, fontWeight: 800, letterSpacing: "-.02em",
            fontSize: { xs: "1.7rem", md: "2.5rem" }, lineHeight: 1.2,
          }}>
            {singleSelect ? tc.titleOne : tc.titleMany}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,.7)", mt: 1, maxWidth: 640, lineHeight: 1.7 }}>
            {singleSelect ? tc.subtitleOne : tc.subtitleMany(maxCourses)}
          </Typography>
          <Typography sx={{
            color: "rgba(255,255,255,.85)", mt: 2, maxWidth: 640, lineHeight: 1.7, fontSize: ".9rem",
            borderLeft: `3px solid ${DP.blueLight}`, pl: 1.5,
          }}>
            {t.courseDetail.seriesNote}
          </Typography>
        </Container>
      </Box>

      {/* ── Leaflets ── */}
      <Container maxWidth="lg" sx={{ mt: { xs: 3, md: 4 } }}>
        {atLimit && (
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: "12px" }}>{tc.limitReached(maxCourses)}</Alert>
        )}

        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map(i => (
              <Grid key={i} size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rounded" height={420} sx={{ borderRadius: "22px" }} />
              </Grid>
            ))}
          </Grid>
        ) : failed ? (
          <Alert severity="error" sx={{ borderRadius: "12px" }}
            action={<Button color="inherit" size="small" onClick={load}>{tc.retry}</Button>}>
            {tc.loadFailed}
          </Alert>
        ) : leaflets.length === 0 ? (
          <Typography sx={{ textAlign: "center", color: DP.textMuted, py: 8 }}>{tc.empty}</Typography>
        ) : (
          <Grid container spacing={3}>
            {leaflets.map(renderCard)}
          </Grid>
        )}
      </Container>

      {/* ── Summary bar ── */}
      <Box sx={{
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1200,
        bgcolor: DP.white, borderTop: `1px solid ${DP.border}`,
        boxShadow: "0 -8px 30px rgba(10,22,40,0.08)",
        pb: "env(safe-area-inset-bottom)",
      }}>
        {/* Right padding keeps the button clear of the floating WhatsApp button */}
        <Container maxWidth="lg" sx={{
          py: 1.75, pr: { xs: "90px", md: "104px" },
          display: "flex", alignItems: "center", gap: 2,
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Stack direction="row" spacing={.5}>
                {Array.from({ length: maxCourses }).map((_, i) => (
                  <Box key={i} sx={{
                    width: 22, height: 6, borderRadius: 99,
                    bgcolor: i < selected.length ? DP.blue : DP.bluePale,
                    transition: "background-color .3s ease",
                  }} />
                ))}
              </Stack>
              <Typography role="status" aria-live="polite" sx={{ fontSize: ".75rem", color: DP.textMuted, fontWeight: 600 }}>
                {tc.counter(selected.length, maxCourses)}
              </Typography>
            </Stack>
            {selected.length > 0 ? (
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: .4, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: ".8rem", fontWeight: 700, color: DP.textMuted }}>{tc.total}</Typography>
                <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, color: DP.textDark, lineHeight: 1.1 }}>${price}</Typography>
                <Typography sx={{ fontSize: ".75rem", color: DP.textMuted, display: { xs: "none", sm: "block" } }}>
                  {tc.totalHint(basePriceOf(planId), withVideo.length, VIDEO_ADDON_PER_COURSE)}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: ".85rem", color: DP.textMuted, mt: .4 }}>{tc.pickPrompt}</Typography>
            )}
          </Box>
          <Button
            variant="contained"
            disabled={selected.length === 0}
            onClick={handleContinue}
            endIcon={<ForwardIcon />}
            sx={{
              flexShrink: 0, borderRadius: "50px", textTransform: "none", fontWeight: 700,
              px: { xs: 2, sm: 3.5 }, py: 1.2, whiteSpace: "nowrap",
              bgcolor: DP.blue, "&:hover": { bgcolor: DP.navy },
              "&.Mui-disabled": { bgcolor: "#E8EDF5", color: "#90A4AE" },
            }}
          >
            {tc.confirm}
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default ChooseLeaflets;
